import Competence from "@/models/Competence.js";
import { requestFactory } from "@socotec.io/socio-vue-components";
import { setDisplayedItems } from "@/utils/setDisplayedItems";
import { list, listAll } from "@/utils/factoryRest";
import { CompetenceApi } from "@socotec.io/certipers-back-sdk";
import configs from "@/setup/openApiConfig";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";

const competencesRepo = useRepo(Competence);
const competenceApi = new CompetenceApi(configs.certipersBackConfig);

const toCompetenceRequest = (competence) => ({
  label: competence.label,
  code: competence.code,
  description: competence.description,
  isArchived: competence.isArchived,
  domains: competence.domains,
});

export const useCompetenceStore = defineStore("competence", {
  state: () => ({
    competencesCount: 0,
  }),

  actions: {
    fetchCompetences: list({
      ModelRepo: competencesRepo,
      restListMethod: competenceApi.list.bind(competenceApi),
      onItemsInserted: ({ results, count }) => {
        useCompetenceStore().setCompetencesCount(count);
        setDisplayedItems(competencesRepo, results);
      },
    }),

    async fetchAllCompetences({ filters = {} } = {}) {
      const results = await listAll(competenceApi.list.bind(competenceApi), filters);

      this.setCompetencesCount(results.length);
      return competencesRepo.save(results);
    },

    async createCompetence(competence) {
      const response = await competenceApi.create({
        competenceRequest: toCompetenceRequest(competence),
      });
      this.addCompetencesCount(1);
      return competencesRepo.save(response);
    },

    async partialUpdateCompetence(competence) {
      const response = await competenceApi.partialUpdate({
        uuid: competence.uuid,
        patchedCompetenceRequest: { isArchived: true },
      });
      return competencesRepo.save(response);
    },

    async deleteCompetence(competence) {
      await competenceApi.destroy({ uuid: competence.uuid });
      this.addCompetencesCount(-1);
      competencesRepo.destroy(competence.uuid);
    },

    async updateCompetence(competence) {
      const response = await competenceApi.update({
        uuid: competence.uuid,
        competenceRequest: toCompetenceRequest(competence),
      });
      return competencesRepo.where("uuid", competence.uuid).update(response);
    },

    setCompetencesCount(count) {
      this.competencesCount = count;
    },

    addCompetencesCount(count) {
      this.competencesCount += count;
    },
  },

  getters: {
    getCompetencesCount: (state) => state.competencesCount,
    getCompetences: () => {
      return requestFactory.getters.paginateGetter(
        competencesRepo.orderBy("createdAt", "desc").withAll(),
      );
    },
    getDisplayedCompetences: () => {
      return competencesRepo.where("isDisplayed", true).orderBy("createdAt", "desc").get();
    },
    getCompetenceCode: () => (competenceUuid) => {
      return competencesRepo.find(competenceUuid)?.code;
    },
  },
});
